import React, { useEffect, useState, useCallback } from 'react';
import styled from 'styled-components';
import { Loader, AlertCircle } from 'lucide-react';
import { UserDto } from '../../services/usersService';
import { partialSupervisorsApi } from '../../services/partialSupervisorService';
import SalesRepPicker from './SalesRepPicker';

interface Props {
  supervisorId: number;
}

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
`;

const SectionTitle = styled.h3`
  margin: 0;
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--text-primary);
`;

const LoadingRow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  color: #6c757d;
  font-size: 0.85rem;
`;

const ErrorRow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  color: #dc2626;
  font-size: 0.85rem;
`;

const Spinner = styled(Loader)`
  animation: spin 0.9s linear infinite;
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const PartialSupervisorManagementSection: React.FC<Props> = ({ supervisorId }) => {
  const [salesReps, setSalesReps] = useState<UserDto[]>([]);
  const [groupName, setGroupName] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const supervisor = await partialSupervisorsApi.getById(supervisorId);
      setSalesReps(supervisor.salesReps || []);
      setGroupName(supervisor.groupName ?? null);
    } catch (err: any) {
      const message = err?.response?.data?.message
        || err?.response?.data
        || 'שגיאה בטעינת הנציגים';
      setError(typeof message === 'string' ? message : 'שגיאה בטעינת הנציגים');
    } finally {
      setLoading(false);
    }
  }, [supervisorId]);

  useEffect(() => {
    load();
  }, [load]);

  const extractErrorMessage = (err: any): string => {
    if (err?.response?.status === 403) return 'אין לך הרשאה לבצע פעולה זו';
    const message = err?.response?.data?.message || err?.response?.data;
    return typeof message === 'string' ? message : 'שגיאה בביצוע הפעולה';
  };

  // שמירה כ-full-replace: ה-endpoint מחליף את כל רשימת הנציגים
  const handlePickerChange = async (next: UserDto[]) => {
    const currentIds = new Set(salesReps.map(s => s.id));
    const nextIds = new Set(next.map(s => s.id));
    const changed = next.length !== salesReps.length
      || next.some(s => !currentIds.has(s.id))
      || salesReps.some(s => !nextIds.has(s.id));

    if (!changed) return;

    // עדכון אופטימי לחוויה חלקה
    setSalesReps(next);
    setBusy(true);
    setError(null);
    try {
      await partialSupervisorsApi.setSalesReps(supervisorId, next.map(s => s.id), groupName);
      await load();
    } catch (err: any) {
      setError(extractErrorMessage(err));
      await load();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Body>
      <SectionTitle>נציגי מכירות מפוקחים</SectionTitle>
      {loading && (
        <LoadingRow>
          <Spinner size={14} />
          טוען נציגים...
        </LoadingRow>
      )}
      {error && (
        <ErrorRow>
          <AlertCircle size={14} />
          {error}
        </ErrorRow>
      )}
      {busy && !loading && (
        <LoadingRow>
          <Spinner size={14} />
          מעדכן...
        </LoadingRow>
      )}
      <SalesRepPicker
        selectedSalesReps={salesReps}
        onChange={handlePickerChange}
        disabled={busy || loading}
      />
    </Body>
  );
};

export default PartialSupervisorManagementSection;
